/* eslint-disable max-len */
import { Vue, Component } from 'vue-property-decorator';

/**
 * UndoClearMixin — Gmail-style undo on input clear
 *
 * When a user clicks the X icon, the field clears instantly and a snackbar
 * appears with an "Undo" button for 5 seconds. Clicking Undo restores the
 * previous value. Only one undo is active at a time (no stacking).
 *
 * Usage:
 *   // Component: extends Mixins(UndoClearMixin)
 *   // Template: @focus="snapshotField('key', val)"
 *   // @click:clear="handleClear('key', v => val = v, currentValue)" — pass current value so undo works
 *   // when the field was empty at focus (user typed then cleared).
 *   // Snackbar: <v-snackbar v-model="undoSnackbar" :timeout="5000" bottom>...</v-snackbar>
 */
@Component
export default class UndoClearMixin extends Vue {
  protected undoSnackbar = false;

  protected undoSnapshots: Record<string, string> = {};

  protected undoBufferKey = '';

  protected undoBufferValue = '';

  protected undoBufferSetter: ((v: string) => void) | null = null;

  protected undoTimer: ReturnType<typeof setTimeout> | null = null;

  protected snapshotField(key: string, value: string): void {
    this.undoSnapshots[key] = value || '';
  }

  /**
   * @param valueBeforeClear — value still in the field when clear is clicked (Vuetify emits
   *   `click:clear` before clearing). If omitted, falls back to the focus snapshot only — which
   *   is empty when the user focused an empty field then typed and cleared (snackbar would never show).
   */
  protected handleClear(
    key: string,
    setter: (v: string) => void,
    valueBeforeClear?: string,
  ): void {
    const fromClick = valueBeforeClear !== undefined && valueBeforeClear !== null
      ? String(valueBeforeClear)
      : undefined;
    const fromFocus = this.undoSnapshots[key];
    const prev = (fromClick !== undefined && fromClick !== '')
      ? fromClick
      : (fromFocus ?? '');
    if (prev === '') return;

    if (this.undoTimer) clearTimeout(this.undoTimer);

    this.undoBufferKey = key;
    this.undoBufferValue = prev;
    this.undoBufferSetter = setter;
    this.undoSnackbar = true;

    this.undoTimer = setTimeout(() => {
      this.undoBufferSetter = null;
      this.undoBufferKey = '';
      this.undoSnackbar = false;
    }, 5000);
  }

  protected undoRestore(): void {
    if (!this.undoBufferSetter) return;

    const { undoBufferValue, undoBufferSetter } = this;
    this.$nextTick(() => {
      undoBufferSetter(undoBufferValue);
      // Reset validation after value is restored to clear stale errors
      this.$nextTick(() => this.onUndoRestored());
    });

    this.undoBufferSetter = null;
    this.undoBufferKey = '';
    this.undoSnackbar = false;
    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
      this.undoTimer = null;
    }
  }

  /** Override in component to reset form validation after undo */
  // eslint-disable-next-line class-methods-use-this
  protected onUndoRestored(): void {
    // no-op by default — component can override
  }

  beforeDestroy(): void {
    if (this.undoTimer) {
      clearTimeout(this.undoTimer);
      this.undoTimer = null;
    }
  }
}
